import type { ReactNode } from 'react'
import { createContext, useContext, useMemo } from 'react'

export interface OrgContextValue {
  /** Subdomain slug (e.g. `acme` from `acme.ohear.ai`), or null on the bare app domain. */
  slug: string | null
  /** True when the current host is `app.ohear.ai` or `localhost` (no tenant slug). */
  isRootHost: boolean
}

const OrgContext = createContext<OrgContextValue | undefined>(undefined)

/**
 * Resolve the tenant slug from `window.location.hostname` once at mount.
 * Rules:
 *   - `app.ohear.ai`            -> { slug: null, isRootHost: true }
 *   - `acme.ohear.ai`           -> { slug: 'acme', isRootHost: false }
 *   - `localhost` / `127.0.0.1` -> { slug: null, isRootHost: true }
 *   - `acme.localhost` (dev)    -> { slug: 'acme', isRootHost: false }
 *
 * The org id itself is fetched server-side from this slug; that lookup lives
 * in `lib/api.ts` callers, not here. This context is purely a routing concern.
 */
export function OrgProvider({ children }: { children: ReactNode }) {
  const value = useMemo<OrgContextValue>(() => resolveOrgFromHost(window.location.hostname), [])
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext)
  if (!ctx) throw new Error('useOrg must be inside OrgProvider')
  return ctx
}

export function resolveOrgFromHost(hostname: string): OrgContextValue {
  // Strip port if present
  const host = hostname.split(':')[0] ?? hostname
  const parts = host.split('.')

  // localhost / 127.0.0.1 with no subdomain
  if (host === 'localhost' || host === '127.0.0.1') return { slug: null, isRootHost: true }

  // <slug>.localhost dev pattern
  if (parts.length === 2 && parts[1] === 'localhost') {
    return { slug: parts[0] ?? null, isRootHost: false }
  }

  // app.ohear.ai (root SPA host) — no tenant
  if (parts.length >= 3 && parts[0] === 'app') return { slug: null, isRootHost: true }

  // <slug>.ohear.ai — tenant subdomain
  if (parts.length >= 3) return { slug: parts[0] ?? null, isRootHost: false }

  return { slug: null, isRootHost: true }
}
